import PropTypes from 'prop-types';
import { memo, useCallback, useState, useRef, Fragment } from 'react';
import { memoizedSequence } from 'UIComponents/core/Functions';
import { MSG_TYPE_RESIZE_MINI_COMMENTER, MSG_TYPE_RESIZED_MINI_COMMENTER, MSG_TYPE_COMMENTS_BROADCAST, MSG_TYPE_CLOSE_MINI_COMMENTER, MSG_TYPE_MINI_COMMENTER_HAS_TEXT, MSG_TYPE_MINI_COMMENTER_HAS_NO_TEXT, MSG_TYPE_UPDATE_COLLABORATION_SIDEBAR_STATE, MSG_TYPE_SET_MINI_COMMENTER_MIN_HEIGHT_FOR_POPOVER, MSG_TYPE_MINI_COMMENTER_LOADED_COMMENTS } from 'collaboration-sidebar/constants/IFrameMessageTypes';
import { IFRAME_INIT_TIMEOUT } from 'collaboration-sidebar/constants/iframe';
import useHandleIFrameError from 'collaboration-sidebar/hooks/useHandleIFrameError';
import useCommentIndicatorsSync from 'collaboration-sidebar/features/deepCommenting/hooks/useCommentIndicatorsSync';
import useShowNotifications from 'collaboration-sidebar/hooks/useShowNotifications';
import { getEmbeddableMiniCommenterUrl } from 'collaboration-sidebar/utils/urls';
import AsyncUIIFrame from 'collaboration-sidebar/components/AsyncUIIFrame';
import MiniCommenterSkeleton from 'collaboration-sidebar/components/MiniCommenterSkeleton';
import useMiniCommenterSize from '../hooks/useMiniCommenterSize';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function EmbeddedCommentIframe({
  collaborationSidebarState,
  handleConfirmDeleteCommentModalMessage,
  moduleId,
  handleReady,
  setModuleReply,
  removeModuleReply,
  broadcastCommentsMessage,
  popoverRef,
  autoFocus,
  onClose,
  closePopoverOnOutsideClickRef,
  metaDescription,
  moduleParentComment,
  onUpdateCollaborationSidebarState
}) {
  const sendMessageRef = useRef();
  const {
    miniCommenterSize,
    onResizeMiniCommenter,
    setMinHeightForPopover
  } = useMiniCommenterSize();
  const [isReady, setIsReady] = useState(false);
  const handleShowNotificationsMessage = useShowNotifications(true, sendMessageRef);
  const {
    handleSidebarInitError,
    handleSidebarReadyError
  } = useHandleIFrameError();
  const {
    objectType,
    objectId,
    ctaUrl,
    appName
  } = collaborationSidebarState;
  const handleSetMinHeightForPopoverMessage = useCallback(({
    payload: {
      type,
      height
    }
  }) => {
    if (type === MSG_TYPE_SET_MINI_COMMENTER_MIN_HEIGHT_FOR_POPOVER) {
      setMinHeightForPopover(height);
    }
  }, [setMinHeightForPopover]);
  const handleMiniCommenterLoadedComments = useCallback(({
    payload: {
      type
    }
  }) => {
    if (type === MSG_TYPE_MINI_COMMENTER_LOADED_COMMENTS) {
      setIsReady(true);
    }
  }, []);

  // Let the mini commenter load and report the true size of the comment list,
  // then we'll resize the iframe to fit it.
  const handleResizeIframeMessage = useCallback(({
    payload: {
      type,
      height
    }
  }) => {
    if (type === MSG_TYPE_RESIZE_MINI_COMMENTER) {
      onResizeMiniCommenter(height);
      if (sendMessageRef.current) {
        // @ts-expect-error ts-migrate(2349) FIXME: This expression is not callable.
        sendMessageRef.current(MSG_TYPE_RESIZED_MINI_COMMENTER);
      }
      // Reposition the popover incase it's placement changed
      // due to resizing the iframe
      setTimeout(() => {
        // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
        if (popoverRef.current) {
          // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
          popoverRef.current.reposition();
        }
      }, 0);
    }
  }, [popoverRef, onResizeMiniCommenter]);
  const handleCloseMessage = useCallback(({
    payload: {
      type
    }
  }) => {
    if (type === MSG_TYPE_CLOSE_MINI_COMMENTER) {
      onClose();
    }
  }, [onClose]);
  const handleCommentsBroadcastMessage = useCallback(({
    payload: {
      type,
      broadcastPayload
    }
  }) => {
    if (type === MSG_TYPE_COMMENTS_BROADCAST) {
      broadcastCommentsMessage(broadcastPayload);
    }
  }, [broadcastCommentsMessage]);
  const handleHasTextMessage = useCallback(({
    payload: {
      type
    }
  }) => {
    if (type === MSG_TYPE_MINI_COMMENTER_HAS_TEXT) {
      // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
      closePopoverOnOutsideClickRef.current = false;
    } else if (type === MSG_TYPE_MINI_COMMENTER_HAS_NO_TEXT) {
      // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
      closePopoverOnOutsideClickRef.current = true;
    }
  }, [closePopoverOnOutsideClickRef]);
  const handleUpdateCollaborationSidebarState = useCallback(({
    payload
  }) => {
    if (payload.type === MSG_TYPE_UPDATE_COLLABORATION_SIDEBAR_STATE) {
      onUpdateCollaborationSidebarState(payload.updates);
    }
  }, [onUpdateCollaborationSidebarState]);
  const {
    handleMessage: handleCommentMessage
  } = useCommentIndicatorsSync({
    setModuleReply,
    removeModuleReply
  });
  const onMessage = memoizedSequence(handleResizeIframeMessage, handleMiniCommenterLoadedComments, handleSetMinHeightForPopoverMessage, handleConfirmDeleteCommentModalMessage, handleShowNotificationsMessage, handleCommentMessage, handleCommentsBroadcastMessage, handleCloseMessage, handleHasTextMessage, handleUpdateCollaborationSidebarState);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [!isReady && /*#__PURE__*/_jsx(MiniCommenterSkeleton, {
      hasModuleParentComment: !!moduleParentComment && !!moduleParentComment.current
    }), /*#__PURE__*/_jsx(AsyncUIIFrame, {
      initTimeout: IFRAME_INIT_TIMEOUT,
      name: "mini-commenter",
      onInitError: handleSidebarInitError,
      onReady: ({
        sendMessage
      }) => {
        handleReady({
          sendMessage
        });
        sendMessageRef.current = sendMessage;
      },
      iframePassthruProps: {
        'data-test-id': 'mini-commenter-embedded-iframe',
        'data-iframe-ready': isReady,
        style: {
          height: `${isReady ? miniCommenterSize : 0}px`,
          width: '100%'
        }
      },
      embeddedPassthruProps: {
        metaDescription,
        moduleParentComment: moduleParentComment && moduleParentComment.current,
        collaborationSidebarState
      },
      onReadyError: handleSidebarReadyError,
      onMessage: onMessage,
      src: getEmbeddableMiniCommenterUrl({
        objectType,
        objectId,
        ctaUrl,
        appName,
        moduleId,
        autoFocus,
        parentCommentId: moduleParentComment && moduleParentComment.current && moduleParentComment.current.id
      })
    })]
  });
}
EmbeddedCommentIframe.propTypes = {
  collaborationSidebarState: PropTypes.object.isRequired,
  handleConfirmDeleteCommentModalMessage: PropTypes.func.isRequired,
  moduleId: PropTypes.string.isRequired,
  handleReady: PropTypes.func.isRequired,
  setModuleReply: PropTypes.func.isRequired,
  removeModuleReply: PropTypes.func.isRequired,
  broadcastCommentsMessage: PropTypes.func.isRequired,
  onUpdateCollaborationSidebarState: PropTypes.func.isRequired,
  popoverRef: PropTypes.oneOfType([PropTypes.shape({
    current: PropTypes.object
  })]),
  autoFocus: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  closePopoverOnOutsideClickRef: PropTypes.oneOfType([PropTypes.shape({
    current: PropTypes.bool
  })]),
  metaDescription: PropTypes.shape({
    translationKey: PropTypes.string.isRequired,
    options: PropTypes.object
  }),
  moduleParentComment: PropTypes.shape({
    current: PropTypes.object
  })
};
export default /*#__PURE__*/memo(EmbeddedCommentIframe);